// SEQ-193: the view logic of the Project performance table that is not the
// grid itself, kept pure so a deno test can prove it (this repo has no
// component render harness, see monthOnMonthRender.test.ts for why):
//
//   - which metric columns By month opens on, and how that set is saved
//     separately from Totals;
//   - which months the by month request covers, and when it is made at all;
//   - how many months By month shows (SEQ-202), and how that choice is saved.
//
// SEQ-202: the month on month change (arrow, +/- percent or points) that used
// to sit under each month's figure is gone. Tom asked for it removed, so there
// is no change indicator anywhere in this view or its exports.

import { monthsInRange } from "./monthOnMonth";
import { PROJECT_MONTH_MAX_MONTHS } from "./projectMonthTable";

// ── Default columns, saved separately from Totals ────────────────────────────

/** What By month opens on: Revenue, Cost, Profit and Profit % (the margin). */
export const BY_MONTH_DEFAULT_COLUMN_KEYS: readonly string[] = ["revenue", "cost", "profit", "profitPercent"];

/** Its own storage key. Totals has no entry here and is never read from or written to this key. */
export const BY_MONTH_COLUMNS_STORAGE_KEY = "pnl-project-performance.by-month-columns.v1";

/**
 * The saved By month set, or the default when nothing valid is saved. Unknown
 * keys are dropped (a column that no longer exists), and an empty saved set
 * falls back to the default rather than opening on an empty grid.
 */
export function parseSavedColumnKeys(raw: string | null, allowedKeys: readonly string[]): string[] {
  if (!raw) return [...BY_MONTH_DEFAULT_COLUMN_KEYS];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...BY_MONTH_DEFAULT_COLUMN_KEYS];
    const kept = parsed.filter((k): k is string => typeof k === "string" && allowedKeys.includes(k));
    return kept.length > 0 ? kept : [...BY_MONTH_DEFAULT_COLUMN_KEYS];
  } catch {
    return [...BY_MONTH_DEFAULT_COLUMN_KEYS];
  }
}

export function serialiseColumnKeys(keys: Iterable<string>): string {
  return JSON.stringify([...keys]);
}

// ── Which months, and when to fetch ─────────────────────────────────────────

/**
 * The months the by month request covers, or null when it cannot be made: an
 * open ended range, an unreadable one, none, or more than 24 months.
 *
 * A `from` part way through a month starts at the NEXT month, because the
 * RPC keeps a (project, month) row only when its first of the month is on or
 * after `from` (pf.period_month >= v_from). Starting at the partial month
 * would draw a column that is always blank.
 *
 * THE 24 MONTH CAP IS APPLIED TO THE CALENDAR MONTHS FROM `from` TO `to`,
 * BEFORE the partial first month is dropped, because that is how the RPC counts
 * (20261006000100: (year(to) - year(from)) * 12 + month(to) - month(from) + 1).
 * Counting after the drop let 2024-09-15 to 2026-09-30 through here (24 whole
 * months) while the server counted 25 and raised 22023.
 */
export function projectMonthRange(from: string | null, to: string | null): string[] | null {
  return projectMonthRangeResult(from, to).months;
}

export type ProjectMonthRangeIssue = "open" | "invalid" | "too-long" | "no-whole-month";

/** Plain words for each reason a by month range cannot be shown. */
export const PROJECT_MONTH_RANGE_MESSAGES: Record<ProjectMonthRangeIssue, string> = {
  open: "By month needs a date range with a start and an end. Choose one in the filters above.",
  invalid: "By month could not read that date range. Choose one in the filters above.",
  "too-long": `By month shows at most ${PROJECT_MONTH_MAX_MONTHS} months. Choose a shorter range in the filters above.`,
  "no-whole-month": "Pick a range that covers at least one whole month.",
};

export function projectMonthRangeResult(
  from: string | null,
  to: string | null,
): { months: string[] | null; issue: ProjectMonthRangeIssue | null } {
  if (from === null || to === null) return { months: null, issue: "open" };
  let months: string[];
  try {
    months = monthsInRange(from, to);
  } catch {
    return { months: null, issue: "invalid" };
  }
  if (months.length > PROJECT_MONTH_MAX_MONTHS) return { months: null, issue: "too-long" };
  if (from.slice(8, 10) !== "01") months = months.slice(1);
  if (months.length === 0) return { months: null, issue: "no-whole-month" };
  return { months, issue: null };
}

export type TableView = "totals" | "months";

export interface ByMonthRequestPlan {
  /** False means no call at all: the Totals view, or a range the RPC refuses. */
  enabled: boolean;
  groupBy: "project_month";
  months: string[] | null;
}

/** One RPC call for By month, and none whatsoever while Totals is showing. */
export function byMonthRequestPlan(view: TableView, from: string | null, to: string | null): ByMonthRequestPlan {
  const months = projectMonthRange(from, to);
  return { enabled: view === "months" && months !== null, groupBy: "project_month", months };
}

/** The key one grain request is identified by, so a held response can be matched to the filters it was fetched for. */
export function grainRequestKey(
  filters: { from: string | null; to: string | null; projectIds: string[]; statuses: string[] },
  groupBy: string,
  metrics: string[],
): string {
  return `${groupBy}|${JSON.stringify(filters)}|${JSON.stringify(metrics)}`;
}

/** True when the rows in hand were fetched for the request now being asked for, so old rows are never drawn under new month columns. */
export function isMonthDataCurrent(dataKey: string | null, requestKey: string): boolean {
  return dataKey !== null && dataKey === requestKey;
}

// ── How many months to show (SEQ-202) ───────────────────────────────────────

// The choices offered. "all" is every month in the page's date range, which is
// what By month showed before this control existed.
export type MonthWindow = 3 | 6 | 12 | "all";
export const MONTH_WINDOW_CHOICES: readonly MonthWindow[] = [3, 6, 12, "all"];
export const DEFAULT_MONTH_WINDOW: MonthWindow = "all";
export const BY_MONTH_WINDOW_STORAGE_KEY = "pnl-project-performance.by-month-window.v1";

export function monthWindowLabel(choice: MonthWindow): string {
  return choice === "all" ? "All" : `Last ${choice}`;
}

/** The saved choice, or the default when nothing valid is saved. */
export function parseSavedMonthWindow(raw: string | null): MonthWindow {
  if (!raw) return DEFAULT_MONTH_WINDOW;
  try {
    const parsed: unknown = JSON.parse(raw);
    return MONTH_WINDOW_CHOICES.find((c) => c === parsed) ?? DEFAULT_MONTH_WINDOW;
  } catch {
    return DEFAULT_MONTH_WINDOW;
  }
}

export function serialiseMonthWindow(choice: MonthWindow): string {
  return JSON.stringify(choice);
}

/** The most recent `choice` months of an ascending month list. "all", or a list no longer than the choice, returns every month. */
export function windowMonths(months: string[], choice: MonthWindow): string[] {
  if (choice === "all" || months.length <= choice) return months;
  return months.slice(months.length - choice);
}

/** True when the window leaves out some months the range covers, so the Total has to be read again for the shorter span. */
export function windowNarrowsRange(months: string[], choice: MonthWindow): boolean {
  return windowMonths(months, choice).length < months.length;
}

/** The Total group's label: "Total" over every month, "Total, last 6 months" when the window cuts the range short. */
export function monthWindowTotalLabel(shownCount: number, narrowed: boolean): string {
  return narrowed ? `Total, last ${shownCount} months` : "Total";
}

// ── Picker and sort guards ───────────────────────────────────────────────────

// The last ticked column cannot be unticked: an empty grid says nothing and the
// user would have to find the picker again to get a column back.
export function canUntickColumn(tickedCount: number, isTicked: boolean): boolean {
  return !(isTicked && tickedCount <= 1);
}

export const LAST_COLUMN_NOTE = "At least one column stays on.";

// The by month sort, corrected when its column is no longer shown (the user
// unticked Profit): fall back to the first visible column so a header always
// carries aria-sort, never a sort by a column nobody can see.
//
// SEQ-202: the same fallback for the group. A sort by a month that the window
// has dropped (Last 3 after sorting by January) falls back to the Total group,
// which is always shown, keeping the metric.
export function effectiveMonthSort(
  sort: { group: string; key: string },
  visibleKeys: readonly string[],
  shownGroups?: readonly string[],
): { group: string; key: string } {
  let { group, key } = sort;
  if (shownGroups && group !== "total" && !shownGroups.includes(group)) group = "total";
  if (visibleKeys.length > 0 && !visibleKeys.includes(key)) key = visibleKeys[0];
  return group === sort.group && key === sort.key ? sort : { group, key };
}
